import { useState } from "react";
import { useParams } from "react-router-dom";
import { Newspaper } from "lucide-react";
import Seo from "@/components/site/Seo";
import { DemoBadge, EmptyState, PageHero } from "@/components/site/kit";
import { NewsCard } from "@/components/site/cards";
import useFetch from "@/hooks/useFetch";
import { fileUrl, fmtDate } from "@/lib/api";
import { NEWS_CATEGORIES } from "@/lib/site";
import NotFound from "./NotFound";

export function News() {
  const [cat, setCat] = useState("");
  const { data: items } = useFetch(`/news${cat ? `?category=${encodeURIComponent(cat)}` : ""}`, []);
  return (
    <>
      <Seo title="News" description="Official news, announcements and media releases from the Telangana–Andhra T10 League." />
      <PageHero testid="news-hero" eyebrow="News" title="League News & Announcements" image="/images/street-cricket.jpg">
        Articles marked <DemoBadge /> are sample content shown until official announcements are published.
      </PageHero>
      <section className="container-x py-14">
        <div className="mb-10 flex gap-2 overflow-x-auto pb-2">
          {["", ...NEWS_CATEGORIES].map((c) => (
            <button key={c || "all"} type="button" onClick={() => setCat(c)} data-testid={`news-filter-${(c || "all").toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${cat === c ? "border-[#00E5FF] bg-[#00E5FF] text-[#0A192F]" : "border-white/15 text-slate-300 hover:border-[#00E5FF]"}`}>
              {c || "All"}
            </button>
          ))}
        </div>
        {items?.length ? <div className="grid gap-6 md:grid-cols-3">{items.map((n, i) => <NewsCard key={n.id} article={n} featured={i === 0 && !cat} />)}</div>
          : <EmptyState testid="news-empty" icon={Newspaper} title="No articles in this category yet" />}
      </section>
    </>
  );
}

export function NewsArticle() {
  const { slug } = useParams();
  const { data: a, loading, error } = useFetch(`/news/${slug}`);
  if (error) return <NotFound />;
  if (loading || !a) return <div className="min-h-screen" />;
  return (
    <article data-testid="news-article" className="pb-24">
      <Seo title={a.seo_title || a.title} description={a.meta_description} image={fileUrl(a.featured_image) || undefined}
        jsonLd={{ "@context": "https://schema.org", "@type": "NewsArticle", headline: a.title, datePublished: a.published_at, author: { "@type": "Person", name: a.author || "League Media Team" } }} />
      <PageHero eyebrow={a.category} title={a.title} image={fileUrl(a.featured_image) || "/images/hero.jpg"}>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-score uppercase tracking-widest">{fmtDate(a.published_at)}</span>
          {a.author && <span>· By {a.author}</span>}
          {a.is_demo && <DemoBadge />}
        </div>
      </PageHero>
      <div className="container-x mt-12 max-w-3xl">
        {a.featured_image && <img src={fileUrl(a.featured_image)} alt={a.title} className="mb-10 aspect-[16/9] w-full rounded-md object-cover" />}
        <div className="prose-league">{a.content.split(/\n{2,}/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}</div>
      </div>
    </article>
  );
}
